import React, { useState } from 'react';
import { useAuth } from '../auth/AuthContext';

/**
 * Persistent "you are acting as someone else" banner.
 *
 * Driven entirely by `impersonating` from GET /api/auth/me, NEVER by a local
 * flag set when impersonation started. A local flag is lost on refresh, which
 * would leave the session still impersonating with nothing on screen saying so
 * -- the single worst failure mode this feature has.
 */
function ImpersonationBanner() {
  const { impersonating, user, realUser, stopImpersonating } = useAuth();
  const [stopping, setStopping] = useState(false);

  if (!impersonating || !user || !realUser) return null;

  const handleStop = async () => {
    setStopping(true);
    try {
      await stopImpersonating();
      // No navigate(): the banner simply stops rendering once /me reports
      // impersonating=false. Redirecting would also throw away whatever page
      // the admin was inspecting, which is usually the reason they impersonated.
      // Deliberately ASYMMETRIC with UsersPage::handleImpersonate, which DOES
      // navigate to "/" on start (issue #221): entering impersonation, the
      // admin wants to see what the other person sees; leaving it, they want
      // back the page they were actually looking at, not wherever they first
      // clicked Impersonate from.
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="impersonation-banner" role="status">
      <span>
        👤 Acting as <strong>{user.full_name}</strong> ({user.email})
        {' '}— signed in as <strong>{realUser.full_name}</strong>
      </span>
      <button className="btn btn-secondary" onClick={handleStop} disabled={stopping}>
        {stopping ? 'Stopping…' : 'Stop impersonating'}
      </button>
    </div>
  );
}

export default ImpersonationBanner;
